import { Context, Effect, Layer } from 'effect';

/** The backend environments the Configurator can talk to. */
export type CoreEnvironment = 'dev' | 'prod' | 'test';

/**
 * Hostnames that resolve to something other than `dev`.
 *
 * Everything absent from this map resolves to `dev`, which deliberately covers `localhost`, the dev site and the `pr-<n>.<appId>.amplifyapp.com` pull request previews. Defaulting to `dev` means an unrecognised host can never reach production data.
 */
const ENVIRONMENTS_BY_HOSTNAME = new Map<string, CoreEnvironment>([
	['aciescore.com', 'prod'],
	['www.aciescore.com', 'prod'],
	['test.aciescore.com', 'test'],
]);

/**
 * What each environment adds between a subdomain and the domain itself.
 *
 * The whole of the difference between the three, in one place, because it is asymmetric and easy to get wrong: dev and test name themselves, and prod is the one that says nothing. Every host the Configurator talks to is spelled the same way, so a second origin should not be a second place to remember that.
 */
const HOST_SUFFIXES: Record<CoreEnvironment, string> = {
	dev: '.dev.aciescore.com',
	prod: '.aciescore.com',
	test: '.test.aciescore.com',
};

/** The origin a subdomain resolves to in one environment, without a trailing slash. */
function originFor(subdomain: string, coreEnvironment: CoreEnvironment): string {
	return `https://${subdomain}${HOST_SUFFIXES[coreEnvironment]}`;
}

/**
 * Derives the backend environment from a browser hostname.
 *
 * The Configurator is built as a standalone module and has no access to the host app's `src/environments/*` files, where the environment is substituted at build time. It is therefore detected at runtime from the URL instead.
 */
export function environmentFromHostname(hostname: string): CoreEnvironment {
	return ENVIRONMENTS_BY_HOSTNAME.get(hostname) ?? 'dev';
}

/** Environment values the Configurator needs at runtime. */
export interface Environment {
	/** The backend environment the current hostname resolves to. */
	readonly coreEnvironment: CoreEnvironment;
	/** Origin every API request is prefixed with, without a trailing slash. */
	readonly apiUrl: string;
	/**
	 * Origin the drawings of priced positions are served from, without a trailing slash.
	 *
	 * A different host from the API and a public one: the engine writes an SVG there when it has priced a position, and it is read with no bearer token and no account header. See `DrawingClient`, which is the only thing that should prefix a request with this.
	 */
	readonly drawingUrl: string;
}

function environmentFor(coreEnvironment: CoreEnvironment): Environment {
	return {
		apiUrl: originFor('api', coreEnvironment),
		coreEnvironment,
		drawingUrl: originFor('drawing-cdn', coreEnvironment),
	};
}

/** Provides the environment the Configurator is currently running in. */
export class EnvironmentService extends Context.Service<EnvironmentService, Environment>()('@acies/platform/EnvironmentService', {
	/** Detects the environment from `window.location.hostname`. */
	make: Effect.sync(() => environmentFor(environmentFromHostname(window.location.hostname))),
}) {
	/** The detected environment, ready to run. */
	static readonly layer: Layer.Layer<EnvironmentService> = Layer.effect(this)(this.make);

	/** Pins the environment explicitly, bypassing hostname detection. */
	static readonly layerCoreEnvironment = (coreEnvironment: CoreEnvironment): Layer.Layer<EnvironmentService> => Layer.succeed(EnvironmentService, EnvironmentService.of(environmentFor(coreEnvironment)));
}
